import React, { useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';

const ChangePasswordDialog = ({ open, onClose, onSubmit }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const [error, setError] = useState('');

    // Field-specific error states
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    const resetForm = () => {
  setCurrentPassword('');
  setNewPassword('');
  setConfirmPassword('');
  setCurrentPasswordError('');
  setNewPasswordError('');
  setConfirmPasswordError('');
  setLoading(false);
};


    const validateForm = () => {
        let isValid = true;

        if (!currentPassword.trim()) {
            setCurrentPasswordError('Current password is required');
            isValid = false;
        } else {
            setCurrentPasswordError('');
        }

        if (!newPassword.trim()) {
            setNewPasswordError('New password is required');
            isValid = false;
        } else if (newPassword.length < 6) {
            setNewPasswordError('Password must be at least 6 characters');
            isValid = false;
        } else {
            setNewPasswordError('');
        }

        if (newPassword !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            isValid = false;
        } else {
            setConfirmPasswordError('');
        }

        return isValid;
    };

    const handleSave = () => {
        if (validateForm()) {
            setLoading(true);
            setError('');
            onSubmit({ currentPassword, newPassword });
            setLoading(false);
            onClose();
            resetForm();
        }
    };

    const handleChange = (field, value) => {
        switch (field) {
            case 'currentPassword':
                setCurrentPassword(value);
                setCurrentPasswordError('');
                break;
            case 'newPassword':
                setNewPassword(value);
                setNewPasswordError('');
                break;
            case 'confirmPassword':
                setConfirmPassword(value);
                setConfirmPasswordError('');
                break;
            default:
                break;
        }
    };

    if (!open) return null;


    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-card rounded-lg p-6 w-full max-w-sm shadow-lg relative">
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="absolute top-2 right-3 text-muted-foreground hover:text-foreground"
                >
                    <X className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-medium mb-4">Edit Password</h2>

                {/* Current Password */}
                <div className="relative mb-4">
                    <Input
                        className={currentPasswordError ? 'border-destructive' : ''}
                        type={showCurrent ? 'text' : 'password'}
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => handleChange('currentPassword', e.target.value)}
                    />
                    <button
                        type="button"
                        aria-label="Toggle current password visibility"
                        onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-3 top-[19px] transform -translate-y-1/2 text-muted-foreground"
                    >
                        {showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    {currentPasswordError && <p className="text-destructive text-sm">{currentPasswordError}</p>}
                </div>

                {/* New Password */}
                <div className="relative mb-4">
                    <Input
                        className={newPasswordError ? 'border-destructive' : ''}
                        type={showNew ? 'text' : 'password'}
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                        type="button"
                        aria-label="Toggle new password visibility"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-[19px] transform -translate-y-1/2 text-muted-foreground"
                    >
                        {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    {newPasswordError && <p className="text-destructive text-sm">{newPasswordError}</p>}
                </div>

                {/* Confirm New Password */}
                <div className="relative mb-4">
                    <Input
                        className={confirmPasswordError ? 'border-destructive' : ''}
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                        type="button"
                        aria-label="Toggle confirm password visibility"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-[19px] transform -translate-y-1/2 text-muted-foreground"
                    >
                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    {confirmPasswordError && <p className="text-destructive text-sm">{confirmPasswordError}</p>}
                </div>

                <div className="mt-4 flex justify-end">
                    <Button
                        onClick={handleSave}
                        type="submit"
                        className="w-full"
                        disabled={loading}
                    >
                        Save Changes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordDialog;
